import React, { useState, useEffect, useCallback } from 'react';
import { formatDisplayName } from '../utils/nameUtils';
import {
  Card,
  Button,
  TextInput,
  Select,
  Alert,
  Badge,
  Checkbox,
  Spinner,
  Modal,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow
} from 'flowbite-react';
import {
  HiSearch,
  HiPlus,
  HiPencil,
  HiTrash,
  HiUser,
  HiUserAdd,
  HiBriefcase,
  HiMail,
  HiPhone,
  HiExternalLink
} from 'react-icons/hi';
import { Link } from 'react-router-dom';
import { staffApi, type Staff } from '../api/staff';
import { usersApi } from '../api/users';
import { leasApi } from '../api/leas';
import { useAuth } from '../contexts/AuthContext';
import type { LEAListItem } from '../types/api';
import { useDebounce } from '../hooks/useDebounce';
import { CreateStaffModal } from '../components/modals/CreateStaffModal';
import { SearchableSelect } from '../components/common/SearchableSelect';
import { AssignmentsManager } from '../components/staff/AssignmentsManager';

export const StaffManagementPage: React.FC = () => {
  // State management
  const [staff, setStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOrganizationId, setFilterOrganizationId] = useState<number | undefined>();
  const [filterActive, setFilterActive] = useState<boolean | undefined>();
  const [filterHasAccount, setFilterHasAccount] = useState<boolean | undefined>();
  const [filterRoleTag, setFilterRoleTag] = useState<string>('');
  const [roleTags, setRoleTags] = useState<Array<{ codeValue: string; shortDescription: string }>>([]);
  const [organizations, setOrganizations] = useState<LEAListItem[]>([]);

  // Selection for bulk actions
  const [selectedStaffIds, setSelectedStaffIds] = useState<number[]>([]);
  const [showBulkLoginModal, setShowBulkLoginModal] = useState(false);
  const [selectedRoleForBulk, setSelectedRoleForBulk] = useState<number | null>(null);

  // Individual action modals
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEnableLoginModal, setShowEnableLoginModal] = useState(false);
  const [showAssignmentsModal, setShowAssignmentsModal] = useState(false);
  const [showCreateStaffModal, setShowCreateStaffModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);

  // Pagination
  const [currentPage, _setCurrentPage] = useState(1);
  const [pageSize] = useState(50);

  const { currentUser } = useAuth();
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Load staff data
  const loadStaff = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await staffApi.list({
        search: debouncedSearchTerm || undefined,
        organizationId: filterOrganizationId,
        isActive: filterActive,
        hasUserAccount: filterHasAccount,
        roleTag: filterRoleTag || undefined,
        includeAssignments: true,
        page: currentPage,
        pageSize,
      });

      // Handle both array response and object response formats
      // Backend currently returns array directly, but we expect {staff: [...]}
      const staffList = Array.isArray(data) ? data : (data.staff || []);
      setStaff(staffList);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load staff');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTerm, filterOrganizationId, filterActive, filterHasAccount, filterRoleTag, currentPage, pageSize]);

  // Load data on mount and when filters change
  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  // Load filter dropdown data on mount
  useEffect(() => {
    const loadFilterData = async () => {
      try {
        const [tags, orgs] = await Promise.all([
          staffApi.getRoleTags(),
          leasApi.list({ limit: 500 })
        ]);
        setRoleTags(tags);
        setOrganizations(orgs);
      } catch (err) {
        console.warn('Failed to load filter data:', err);
      }
    };
    loadFilterData();
  }, []);

  // Handle selection changes
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedStaffIds(staff.map(s => s.staffId));
    } else {
      setSelectedStaffIds([]);
    }
  };

  const handleSelectStaff = (staffId: number, checked: boolean) => {
    if (checked) {
      setSelectedStaffIds(prev => [...prev, staffId]);
    } else {
      setSelectedStaffIds(prev => prev.filter(id => id !== staffId));
    }
  };

  // Handle bulk enable login
  const handleBulkEnableLogin = async () => {
    if (!selectedRoleForBulk) {
      setError('Please select a role for the new user accounts');
      return;
    }

    try {
      setLoading(true);
      const result = await staffApi.bulkEnableLogin({
        staffIds: selectedStaffIds,
        roleId: selectedRoleForBulk,
        sendWelcomeEmail: false,
      });

      if (result.successCount > 0) {
        setSuccessMessage(
          `Successfully created ${result.successCount} user accounts. ` +
          `${result.failedCount} failed.`
        );
      }

      // Refresh staff list
      await loadStaff();

      // Clear selections
      setSelectedStaffIds([]);
      setShowBulkLoginModal(false);
      setSelectedRoleForBulk(null);

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enable logins');
    } finally {
      setLoading(false);
    }
  };

  // Handle individual staff actions
  const handleEditStaff = (staffId: number) => {
    const staffMember = staff.find(s => s.staffId === staffId);
    if (staffMember) {
      setSelectedStaff(staffMember);
      setShowEditModal(true);
    }
  };

  const handleDeleteStaff = async (staffId: number) => {
    if (!confirm('Are you sure you want to delete this staff member?')) {
      return;
    }

    try {
      await staffApi.delete(staffId);
      setSuccessMessage('Staff member deleted successfully');
      await loadStaff();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete staff member');
    }
  };

  const handleEnableLogin = (staffId: number) => {
    const staffMember = staff.find(s => s.staffId === staffId);
    if (staffMember) {
      setSelectedStaff(staffMember);
      setShowEnableLoginModal(true);
    }
  };

  const handleManageAssignments = (staffId: number) => {
    const staffMember = staff.find(s => s.staffId === staffId);
    if (staffMember) {
      setSelectedStaff(staffMember);
      setShowAssignmentsModal(true);
    }
  };

  // Get primary assignment for display
  const getPrimaryAssignment = (staffMember: Staff) => {
    return staffMember.assignments.find(a => a.isPrimary) || staffMember.assignments[0];
  };

  // Get primary email for display
  const getPrimaryEmail = (staffMember: Staff) => {
    return staffMember.person.emails?.find(e => e.isPrimary)?.emailAddress ||
           staffMember.person.emails?.[0]?.emailAddress;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Staff Management</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage staff members, assignments, and user accounts</p>
        </div>

        {/* Alerts */}
        {error && (
          <Alert color="failure" onDismiss={() => setError(null)} className="mb-4">
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert color="success" onDismiss={() => setSuccessMessage(null)} className="mb-4">
            {successMessage}
          </Alert>
        )}

        {/* Filters and Actions Bar */}
        <Card className="mb-6">
          <div className="space-y-4">
            {/* Search and Filters Row */}
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <TextInput
                  icon={HiSearch}
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <Select
                value={filterActive === undefined ? '' : filterActive.toString()}
                onChange={(e) => setFilterActive(
                  e.target.value === '' ? undefined : e.target.value === 'true'
                )}
                className="min-w-[120px]"
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Select>

              <Select
                value={filterHasAccount === undefined ? '' : filterHasAccount.toString()}
                onChange={(e) => setFilterHasAccount(
                  e.target.value === '' ? undefined : e.target.value === 'true'
                )}
                className="min-w-[130px]"
              >
                <option value="">All Accounts</option>
                <option value="true">Has Login</option>
                <option value="false">No Login</option>
              </Select>

              <SearchableSelect
                ariaLabel="Filter by organization"
                options={organizations.map(org => ({ value: org.educationOrganizationId, label: org.nameOfInstitution }))}
                value={filterOrganizationId ?? null}
                onChange={(orgId) => setFilterOrganizationId(orgId ?? undefined)}
                clearLabel="All Organizations"
                className="min-w-[220px]"
              />

              <SearchableSelect
                ariaLabel="Filter by role"
                options={roleTags.map(tag => ({ value: tag.codeValue, label: tag.shortDescription }))}
                value={filterRoleTag || null}
                onChange={(code) => setFilterRoleTag(code ?? '')}
                clearLabel="All Roles"
                className="min-w-[200px]"
              />
            </div>

            {/* Action Buttons Row */}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={() => setShowCreateStaffModal(true)}>
                <HiPlus className="mr-2 h-4 w-4" />
                Add Staff
              </Button>

              <Button
                size="sm"
                color="gray"
                disabled={selectedStaffIds.length === 0}
                onClick={() => setShowBulkLoginModal(true)}
              >
                <HiUserAdd className="mr-2 h-4 w-4" />
                Enable Logins ({selectedStaffIds.length})
              </Button>
            </div>
          </div>
        </Card>

        {/* Staff Table */}
        <Card>
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <Spinner size="xl" />
            </div>
          ) : staff.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              No staff members found. Try adjusting your filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table hoverable>
                <TableHead>
                  <TableHeadCell>
                    <Checkbox
                      checked={selectedStaffIds.length === staff.length && staff.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </TableHeadCell>
                  <TableHeadCell>Name</TableHeadCell>
                  <TableHeadCell>Organization</TableHeadCell>
                  <TableHeadCell>Position</TableHeadCell>
                  <TableHeadCell>Contact</TableHeadCell>
                  <TableHeadCell>Account</TableHeadCell>
                  <TableHeadCell>Status</TableHeadCell>
                  <TableHeadCell>Actions</TableHeadCell>
                </TableHead>
                <TableBody className="divide-y">
                  {staff.map((staffMember) => {
                    const primaryAssignment = getPrimaryAssignment(staffMember);
                    const primaryEmail = getPrimaryEmail(staffMember);
                    const primaryPhone = staffMember.person.phones?.find(p => p.isPrimary)?.phoneNumber;

                    return (
                      <TableRow key={staffMember.staffId} className="bg-white dark:bg-gray-800">
                        <TableCell>
                          <Checkbox
                            checked={selectedStaffIds.includes(staffMember.staffId)}
                            onChange={(e) => handleSelectStaff(staffMember.staffId, e.target.checked)}
                          />
                        </TableCell>

                        <TableCell>
                          <div className="flex items-center">
                            <HiUser className="mr-2 h-5 w-5 text-gray-400 dark:text-gray-500" />
                            <div>
                              <Link
                                to={`/persons/${staffMember.person.personId}`}
                                className="font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline flex items-center gap-1"
                              >
                                {formatDisplayName({ ...staffMember.person, lastName: staffMember.person.lastSurname })}
                                <HiExternalLink className="h-3 w-3 opacity-50" />
                              </Link>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                ID: {staffMember.staffId}
                              </div>
                            </div>
                          </div>
                        </TableCell>

                        <TableCell>
                          {primaryAssignment ? (
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {primaryAssignment.organizationName}
                              </div>
                              {staffMember.assignments.length > 1 && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  +{staffMember.assignments.length - 1} more
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">No assignment</span>
                          )}
                        </TableCell>

                        <TableCell>
                          {primaryAssignment?.positionTitle ? (
                            <span className="text-gray-900 dark:text-white">{primaryAssignment.positionTitle}</span>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">—</span>
                          )}
                        </TableCell>

                        <TableCell>
                          <div className="space-y-1 text-sm">
                            {primaryEmail && (
                              <div className="flex items-center text-gray-600 dark:text-gray-300">
                                <HiMail className="mr-1 h-3 w-3" />
                                {primaryEmail}
                              </div>
                            )}
                            {primaryPhone && (
                              <div className="flex items-center text-gray-600 dark:text-gray-300">
                                <HiPhone className="mr-1 h-3 w-3" />
                                {primaryPhone}
                              </div>
                            )}
                          </div>
                        </TableCell>

                        <TableCell>
                          {staffMember.hasUserAccount ? (
                            <Badge color="success">Active Login</Badge>
                          ) : staffMember.userAccountId ? (
                            <Badge color="warning">Inactive Login</Badge>
                          ) : (
                            <Badge color="gray">No Login</Badge>
                          )}
                        </TableCell>

                        <TableCell>
                          {staffMember.isActive ? (
                            <Badge color="success">Active</Badge>
                          ) : (
                            <Badge color="failure">Inactive</Badge>
                          )}
                        </TableCell>

                        <TableCell>
                          <div className="flex space-x-2">
                            <Button
                              size="xs"
                              color="gray"
                              onClick={() => handleEditStaff(staffMember.staffId)}
                              aria-label="Edit Staff"
                            >
                              <HiPencil className="h-3 w-3" />
                            </Button>

                            <button
                              onClick={() => handleManageAssignments(staffMember.staffId)}
                              title="Manage Assignments"
                              aria-label="Manage Assignments"
                              className="inline-flex items-center justify-center px-2 py-1.5 text-xs font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg"
                            >
                              <HiBriefcase className="h-3 w-3" />
                            </button>

                            {!staffMember.userAccountId && (
                              <button
                                onClick={() => handleEnableLogin(staffMember.staffId)}
                                title="Enable Login"
                                aria-label="Enable Login"
                                className="inline-flex items-center justify-center px-2 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
                              >
                                <HiUserAdd className="h-3 w-3" />
                              </button>
                            )}

                            <Button
                              size="xs"
                              color="failure"
                              onClick={() => handleDeleteStaff(staffMember.staffId)}
                              aria-label="Delete Staff"
                            >
                              <HiTrash className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>

        {/* Bulk Enable Login Modal */}
        <Modal
          show={showBulkLoginModal}
          onClose={() => setShowBulkLoginModal(false)}
          dismissible
          popup
        >
          <div className="p-6">
            <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-4">
              Enable User Logins
            </h3>

            <p className="text-gray-600 dark:text-gray-400 mb-4">
              You are about to create user accounts for {selectedStaffIds.length} staff members.
              Select the role to assign to these users:
            </p>

            <div className="mb-6">
              <Label htmlFor="role" className="mb-2">
                User Role
              </Label>
              <Select
                id="role"
                value={selectedRoleForBulk || ''}
                onChange={(e) => setSelectedRoleForBulk(Number(e.target.value))}
              >
                <option value="">Select a role...</option>
                <option value="3">Viewer (Read-only)</option>
                <option value="2">Organization Admin</option>
                {currentUser?.role === 'superAdmin' && (
                  <option value="1">Super Admin</option>
                )}
              </Select>
            </div>

            <div className="flex justify-end space-x-2">
              <Button
                color="gray"
                onClick={() => setShowBulkLoginModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleBulkEnableLogin}
                disabled={!selectedRoleForBulk || loading}
              >
                {loading ? <Spinner size="sm" /> : 'Enable Logins'}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Edit Staff Modal */}
        <Modal
          show={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedStaff(null);
          }}
          dismissible
          popup
          size="lg"
        >
          <div className="p-6">
            <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-4">
              Edit Staff Member
            </h3>

            {selectedStaff && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName" className="mb-2">
                      First Name
                    </Label>
                    <TextInput
                      id="firstName"
                      value={selectedStaff.person.firstName}
                      onChange={(e) => {
                        setSelectedStaff({
                          ...selectedStaff,
                          person: {
                            ...selectedStaff.person,
                            firstName: e.target.value
                          }
                        });
                      }}
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName" className="mb-2">
                      Last Name
                    </Label>
                    <TextInput
                      id="lastName"
                      value={selectedStaff.person.lastSurname}
                      onChange={(e) => {
                        setSelectedStaff({
                          ...selectedStaff,
                          person: {
                            ...selectedStaff.person,
                            lastSurname: e.target.value
                          }
                        });
                      }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="middleName" className="mb-2">
                      Middle Name (Optional)
                    </Label>
                    <TextInput
                      id="middleName"
                      value={selectedStaff.person.middleName || ''}
                      onChange={(e) => {
                        setSelectedStaff({
                          ...selectedStaff,
                          person: {
                            ...selectedStaff.person,
                            middleName: e.target.value || undefined
                          }
                        });
                      }}
                    />
                  </div>
                  <div>
                    <Label htmlFor="preferredName" className="mb-2">
                      Preferred Name (Optional)
                    </Label>
                    <TextInput
                      id="preferredName"
                      value={selectedStaff.person.preferredName || ''}
                      onChange={(e) => {
                        setSelectedStaff({
                          ...selectedStaff,
                          person: {
                            ...selectedStaff.person,
                            preferredName: e.target.value || undefined
                          }
                        });
                      }}
                    />
                  </div>
                </div>

                <div>
                  <Label className="mb-2">Status</Label>
                  <div className="flex items-center space-x-4">
                    <Checkbox
                      id="isActive"
                      checked={selectedStaff.isActive}
                      onChange={(e) => {
                        setSelectedStaff({
                          ...selectedStaff,
                          isActive: e.target.checked
                        });
                      }}
                    />
                    <Label htmlFor="isActive">Active</Label>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-2 mt-6">
              <Button
                color="gray"
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedStaff(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  if (!selectedStaff) return;
                  try {
                    await staffApi.update(selectedStaff.staffId, {
                      isActive: selectedStaff.isActive
                    });
                    setSuccessMessage('Staff member updated successfully');
                    setShowEditModal(false);
                    setSelectedStaff(null);
                    await loadStaff();
                    setTimeout(() => setSuccessMessage(null), 3000);
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'Failed to update staff member');
                  }
                }}
                disabled={loading}
              >
                {loading ? <Spinner size="sm" /> : 'Save Changes'}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Enable Login Modal */}
        <Modal
          show={showEnableLoginModal}
          onClose={() => {
            setShowEnableLoginModal(false);
            setSelectedStaff(null);
          }}
          dismissible
          popup
        >
          <div className="p-6">
            <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-4">
              Enable User Login
            </h3>

            {selectedStaff && (
              <>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Create a user account for <strong className="text-gray-900 dark:text-white">{formatDisplayName({ ...selectedStaff.person, lastName: selectedStaff.person.lastSurname })}</strong>
                </p>

                <div className="mb-4">
                  <Label htmlFor="singleRole" className="mb-2">
                    Select Role
                  </Label>
                  <Select
                    id="singleRole"
                    value={selectedRoleForBulk || ''}
                    onChange={(e) => setSelectedRoleForBulk(Number(e.target.value))}
                  >
                    <option value="">Select a role...</option>
                    <option value="3">Viewer (Read-only)</option>
                    <option value="2">Organization Admin</option>
                    {currentUser?.permissions?.includes('system_admin') && (
                      <option value="1">Super Admin</option>
                    )}
                  </Select>
                </div>

                <div className="flex justify-end space-x-2">
                  <Button
                    color="gray"
                    onClick={() => {
                      setShowEnableLoginModal(false);
                      setSelectedStaff(null);
                      setSelectedRoleForBulk(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={async () => {
                      if (!selectedRoleForBulk || !selectedStaff) return;
                      try {
                        await usersApi.create({
                          personId: selectedStaff.person.personId,
                          roleId: selectedRoleForBulk,
                          isActive: true
                        });
                        setSuccessMessage('User account created successfully');
                        setShowEnableLoginModal(false);
                        setSelectedStaff(null);
                        setSelectedRoleForBulk(null);
                        await loadStaff();
                        setTimeout(() => setSuccessMessage(null), 3000);
                      } catch (err) {
                        setError(err instanceof Error ? err.message : 'Failed to create user account');
                      }
                    }}
                    disabled={!selectedRoleForBulk || loading}
                  >
                    {loading ? <Spinner size="sm" /> : 'Create Account'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </Modal>

        {/* Manage Assignments Modal */}
        <Modal
          show={showAssignmentsModal}
          onClose={() => {
            setShowAssignmentsModal(false);
            setSelectedStaff(null);
          }}
          dismissible
          size="3xl"
        >
          <div className="p-6">
            {selectedStaff && (
              <>
                {/* Header with close button */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-medium text-gray-900 dark:text-white">
                    Manage Assignments: {formatDisplayName({ ...selectedStaff.person, lastName: selectedStaff.person.lastSurname })}
                  </h3>
                  <button
                    onClick={() => {
                      setShowAssignmentsModal(false);
                      setSelectedStaff(null);
                    }}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                    aria-label="Close"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <AssignmentsManager
                  staffId={selectedStaff.staffId}
                  assignments={selectedStaff.assignments}
                  onUpdate={async () => {
                    await loadStaff();
                    // Refresh the selected staff with updated data
                    const updatedStaff = await staffApi.get(selectedStaff.staffId);
                    setSelectedStaff(updatedStaff);
                  }}
                />

                {/* Footer with Done button */}
                <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                  <Button
                    color="gray"
                    onClick={() => {
                      setShowAssignmentsModal(false);
                      setSelectedStaff(null);
                    }}
                  >
                    Done
                  </Button>
                </div>
              </>
            )}
          </div>
        </Modal>

        {/* Create Staff Modal */}
        <CreateStaffModal
          isOpen={showCreateStaffModal}
          onClose={() => setShowCreateStaffModal(false)}
          onSuccess={async ({ staffId, personId: _personId }) => {
            // Close create modal
            setShowCreateStaffModal(false);

            // Reload staff list
            await loadStaff();

            // Open assignments modal for the newly created staff
            const newStaff = await staffApi.get(staffId);
            setSelectedStaff(newStaff);
            setShowAssignmentsModal(true);
          }}
          modalTitle="Add Staff Member"
        />
      </div>
    </div>
  );
};